import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import './userInfo.scss';
import {
  IconAccountCircle,
  IconArrowDropDown,
  IconArrowDropUp,
  TextButton,
  Menu,
  MenuListItem,
  IconHome,
  IconDashboard,
  IconTableSettings,
  IconDescription,
  IconViewList,
  IconOrder,
  IconApps,
  IconHelp,
  IconNoteAdd,
  IconLogout,
  Divider,
} from 'cdk-radial';
import { useOktaAuth } from '@okta/okta-react';
import { setScreen } from '../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../config/uiFactory.config';
import { ScreenPaths } from '../../constants/constants';
import { addNavListener } from '../../utils/popstate';
import { sendAmplitudeData } from '../../utils/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants/amplitudeConstants';
import { environmentURLs } from '../common/CaptureEnvironment';
import { resetConfigurations } from '../../redux/connectedComponent/configurations/Configurations.slice';
import { resetState } from '../../redux/connectedComponent/ManageIntegrationsV2/ManageIntegrationsV2.slice';
import { openAha } from '@cdk-prod/fortellis-aha-widget';
import { ReactComponent as IconBulbOutline } from '../../assets/IconBulbOutline.svg';
import { setAPIDirectoryPage } from '../../redux/connectedComponent/apiDirectory/apiDirectory.slice';

const {
  MANAGEMENT,
  TRANSACTION_REPORT,
  API_DIRECTORY,
  INTEGRATION_REPORT,
  PURCHASE_HISTORY,
  HOME,
  MY_APIS,
} = ScreenPaths;

const UserInfoIconArrowDropDown = styled(IconArrowDropDown)`
  fill: ${({ theme }) => theme.color.additional.alwaysWhite.value};
`;

const UserInfoIconArrowDropUp = styled(IconArrowDropUp)`
  fill: ${({ theme }) => theme.color.additional.alwaysWhite.value};
`;

const UserInfo = (props) => {
   const state = useSelector(state => state);
    const {
      hasDAABAccess
    } = state.loginDetailsSlice;
  const dropdownOptionsRef = useRef(null);
  const dispatch = useDispatch();
  const { oktaAuth } = useOktaAuth();

  const path = props.location?.pathname?.replace(/\/$/, '');
  const isTransactionReportUrl = path === `/dp/${TRANSACTION_REPORT}`;
  const isSubManagementUrl = path === `/dp/${MANAGEMENT}`;
  const isIntegrationReportUrl = path === `/dp/${INTEGRATION_REPORT}`;
  const isAPIDirectoryUrl = path === `/dp/${API_DIRECTORY}`;
  const isDashboardUrl = path === `/dp/${HOME}`;
  const isMyApisUrl = path === `/dp/${MY_APIS}`;

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setIsOpen((prevState) => !prevState);
  };

  const isAhaEnabled =
    process.env.REACT_APP_FEATURE_AHA_WIDGET_ENABLED === 'true';
  
  const isCaseManagementEnabled =
    process.env.REACT_APP_NAV_CASE_MANAGEMENT_ENABLED === 'true';

  const options = [
    {
      label: 'App Management',
      ...(isSubManagementUrl ? { isSelected: true } : {}),
    },
    {
      label: 'Data Transaction Report',
      ...(isTransactionReportUrl ? { isSelected: true } : {}),
    },
    {
      label: 'Dealer Integrations',
      ...(isIntegrationReportUrl ? { isSelected: true } : {}),
    },
    ...(isAhaEnabled
      ? [
          {
            label: 'Submit Ideas',
            isSelected: false,
          },
        ]
      : []),
    //{label: 'Purchase History'} //TODO: Enable Purchase History screen if needed
  ];

  if (window.top === window.self) {
    options.push({ label: 'Sign Out' });
  }

  const groupedOptions = [
    {
      id: 'group-1',
      items: [
        {
          label: 'Home',
          icon: <IconHome />,
          ...(isDashboardUrl ? { isSelected: true } : {}),
        },
        {
          label: 'My APIs',
          icon: <IconDashboard />,
          ...(isMyApisUrl ? { isSelected: true } : {}),
        },
        {
          label: 'My Apps',
          icon: <IconTableSettings />,
          ...(isSubManagementUrl ? { isSelected: true } : {}),
        },
        {
          label: 'Data Transaction Report',
          icon: <IconDescription />,
          ...(isTransactionReportUrl ? { isSelected: true } : {}),
        },
        {
          label: 'Dealer Integrations',
          icon: <IconViewList />,
          ...(isIntegrationReportUrl ? { isSelected: true } : {}),
        },
      ],
    },
    {
      id: 'group-2',
      items: [
        {
          label: 'API Directory',
          icon: <IconOrder />,
          ...(isAPIDirectoryUrl ? { isSelected: true } : {}),
        },
        {
          label: 'App Marketplace',
          icon: <IconApps />,
          isSelected: false,
        },
      ],
    },
    {
      id: 'group-3',
      items: [
        ...(isAhaEnabled
          ? [
              {
                label: 'Submit an Idea',
                isSelected: false,
                icon: <IconBulbOutline />,
              },
            ]
          : []),
        ...(isCaseManagementEnabled
          ? [
              {
                label: 'Submit/Review a Case',
                isSelected: false,
                icon: <IconNoteAdd />,
              },
            ]
          : []),
        {
          label: 'Help and Documents',
          isSelected: false,
          icon: <IconHelp />,
        },
      ],
    },
  ];

  if (window.top === window.self) {
    groupedOptions.push({
      id: 'group-4',
      items: [
        {
          label: 'Sign Out',
          isSelected: false,
          icon: <IconLogout />,
        },
      ],
    });
  }

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState({});

  const onMenuClickHandler = (option) => {
    setIsOpen(false);
    setSelectedOption(option);
    dropdownOptionsRef.current.focus();
    addNavListener(dispatch);
    // Standardized navigation using React Router's history instead of window.history.pushState.
    // This ensures that the global GTM history listener in App.js is correctly triggered.
    switch (option?.label) {
      case 'My Apps':
      case 'App Management':
        sendAmplitudeData(AMPLITUDE_EVENTS.VIEW_APP_MANAGEMNET, {});
        props.history.push({
          pathname: `/dp/${MANAGEMENT}`,
          state: { screen: screens.SUBSCRIPTION_MANAGEMENT }
        });
        dispatch(resetConfigurations());
        dispatch(resetState());
        dispatch(setScreen({ screen: screens.SUBSCRIPTION_MANAGEMENT }));
        break;
      case 'API Directory':
        localStorage.removeItem('componentToRender');
        localStorage.removeItem('apiResponse');
        dispatch(setAPIDirectoryPage({ page: 'apiDirHome' }));
        sendAmplitudeData(AMPLITUDE_EVENTS.VIEW_API_DIRECTORY, {});
        props.history.push({
          pathname: `/dp/${API_DIRECTORY}`,
          state: { screen: screens.API_DIRECTORY }
        });
        dispatch(setScreen({ screen: screens.API_DIRECTORY }));
        break;
      case 'Home':
        sendAmplitudeData(AMPLITUDE_EVENTS.VIEW_DASHBOARD, {});
        props.history.push({
          pathname: `/dp/${HOME}`,
          state: { screen: screens.DASHBOARD }
        });
        dispatch(setScreen({ screen: screens.DASHBOARD }));
        break;
      case 'My APIs':
        sendAmplitudeData(AMPLITUDE_EVENTS.VIEW_MY_APIS, {});
        props.history.push({
          pathname: `/dp/${MY_APIS}`,
          state: { screen: screens.MY_APIS }
        });
        dispatch(setScreen({ screen: screens.MY_APIS }));
        break;

      case 'Data Transaction Report':
        sendAmplitudeData(AMPLITUDE_EVENTS.VIEW_DATA_TRANSACTION_REPORT, {});
        props.history.push({
          pathname: `/dp/${TRANSACTION_REPORT}`,
          state: { screen: screens.TRANSACTION_REPORT }
        });

        dispatch(setScreen({ screen: screens.TRANSACTION_REPORT }));
        break;
      case 'Dealer Integrations':
        sendAmplitudeData(AMPLITUDE_EVENTS.VIEW_DEALER_INTEGRATIONS, {});
        props.history.push({
          pathname: `/dp/${INTEGRATION_REPORT}`,
          state: { screen: screens.INTEGRATION_REPORT }
        });
        dispatch(setScreen({ screen: screens.INTEGRATION_REPORT }));
        break;

      case 'Purchase History':
        props.history.push({
          pathname: `/dp/${PURCHASE_HISTORY}`,
          state: { screen: screens.DEALER_PURCHASE_REPORT }
        });

        dispatch(setScreen({ screen: screens.DEALER_PURCHASE_REPORT }));
        break;
      case 'Sign Out':
        sendAmplitudeData(AMPLITUDE_EVENTS.USER_SIGNOUT, {});
        let token = oktaAuth?.authStateManager?._authState?.idToken?.idToken;
        localStorage.clear('state');
        if (token) {
          window.location.href = `${process.env.REACT_APP_LOGOUT_BASE}?id_token_hint=${token}`;
        } else {
          window.location.href = process.env.REACT_APP_LOGOUT_BASE;
        }
        break;
      case 'Submit Ideas':
      case 'Submit an Idea':
        const campaignId =
          process.env.REACT_APP_PLATFORM_CAMPAIGNID_DEALERPORTAL;
        const isAhaEnabled =
          process.env.REACT_APP_FEATURE_AHA_WIDGET_ENABLED === 'true';
        openAha(campaignId, isAhaEnabled);
        break;
      case 'Submit/Review a Case':
        window.open(
          environmentURLs.REACT_APP_NAV_CASE_MANAGEMENT_URL,
          '_blank',
          'noopener'
        );
        break;
      case 'App Marketplace':
        window.open(
          environmentURLs.REACT_APP_MARKETPLACE_URL,
          '_blank',
          'noopener'
        );
        break;
      case 'Help and Documents':
        window.open(
          environmentURLs.REACT_APP_NAV_DOCS_URL,
          '_blank',
          'noopener'
        );
        break;
      default:
        return;
    }
  };

  return (
    <>
      <div
        id="userinfo-container-id"
        className="userinfo-container"
        ref={dropdownOptionsRef}
      >
        <div
          id="userinfo-buttton-id"
          data-testid="userinfo-buttton-id"
          onClick={handleMenuClick}
        >
          <TextButton
            icon={<IconAccountCircle />}
            text={<UserInfoButton email={props.email} isOpen={isOpen} />}
            backgroundAppearance={'dark'}
            className="userinfo-button"
            id="userinfo-button"
          ></TextButton>
        </div>

        <Menu
          dataTestId="menu-container"
          labelRef={dropdownOptionsRef}
          className="user-info-menu"
        >
          {hasDAABAccess === false
            ? options.map((option) => (
              <MenuListItem
                dataTestId={`${option?.label}-option`}
                isSelected={option?.isSelected}
                key={`menu-${option.label}`}
                onClick={() => onMenuClickHandler(option)}
                hideSelectedItemCheckmark={true}
              >
                {option?.label}
              </MenuListItem>
            ))
            : groupedOptions.map((options, index) => (
              <div id={index} key={options.id || `group-${index}`}>
                <div>
                  {options?.items?.map((option, index) => (
                    <MenuListItem
                      id={`${option?.label}-grouped-option`}
                      dataTestId={`${option?.label}-grouped-option`}
                      isSelected={option?.isSelected}
                      key={`${option?.label}-${index}`}
                      onClick={() => onMenuClickHandler(option)}
                      hideSelectedItemCheckmark={true}
                      graphicContent={option.icon}
                    >
                      {option?.label}
                    </MenuListItem>
                  ))}
                </div>
                {index < groupedOptions.length - 1 ? (
                  <Divider id={index} />
                ) : (
                  <div />
                )}
              </div>
            ))}
        </Menu>
      </div>
    </>
  );
};

function UserInfoButton({ email, isOpen }) {
  return (
    <div className="userinfo-button-container">
      {email}
      {isOpen ? <UserInfoIconArrowDropUp /> : <UserInfoIconArrowDropDown />}
    </div>
  );
}

export default withRouter(UserInfo);
